import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
  HttpErrorResponse,
  HttpInterceptor,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  Observable,
  map,
  catchError,
  throwError,
  switchMap,
  take,
  BehaviorSubject,
  filter,
  EMPTY,
  Subject,
  finalize,
} from 'rxjs';
import { filter as rxjsFilter } from 'rxjs/operators';
import { Router, NavigationEnd } from '@angular/router';
import { AuthService } from '../services/api/auth.service';
import { Store } from '@ngrx/store';
import { selectToken } from '../../store/selectors/token.selectors';
import { selectLocale } from '../../store/selectors/index.selectors';
import { setLoading } from '../../store/actions/loading.actions';
import { TranslateService } from '@ngx-translate/core';
// import { ROUTES } from '../../shared/constants';
import { signInSuccess } from '../../store/actions/auth.actions';
import { formatGuarantorErrorPayload } from '../utils/guarantor-api.utils';

@Injectable({
  providedIn: 'root',
})
export class InterceptorService implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);
  private currentLang: string = 'en';
  private logoutSubject = new Subject<void>();
  private isLoggingOut = false;

  constructor(
    private _router: Router,
    private _store: Store<any>,
    private _translate: TranslateService,
    private _authService: AuthService
  ) {
    // Locale subscription
    this._store.select(selectLocale).subscribe((locale) => {
      this.currentLang = locale;
    });

    // Reset logout state on navigation
    this._router.events
      .pipe(rxjsFilter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        if (event.url.includes('/dashboard') || event.url.includes('/admin')) {
          this.resetLogoutState();
        }
      });

    // Logout logic
    this.logoutSubject.pipe(
      switchMap(() => {
        if (this.isLoggingOut) {
          return EMPTY;
        }
        this.isLoggingOut = true;
        this._authService.logout();
        return EMPTY;
      })
    ).subscribe();
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this._store.select(selectToken).pipe(
      take(1),
      switchMap((token) => {
        const req = this.addHeaders(request, token, this.currentLang);
        return next.handle(req).pipe(
          map((event: HttpEvent<any>) => {
            if (event instanceof HttpResponse && event.body?.success === false) {
              // Handle success logic if needed
            }
            return event;
          }),
          catchError((error: HttpErrorResponse) => {
            const isRefreshRequest = request.url.includes(this._authService.refreshTokenEndPoint);
            const isAuthEndpoint =
              req.url.includes('/login') ||
              req.url.includes('/mfa') ||
              req.url.includes('/forgot-password') ||
              req.url.includes('/reset-password');

            // Handle 401 Unauthorized
            if (error.status === 401) {
              if (isAuthEndpoint) {
                return throwError(() => ({
                  status: 401,
                  message: error.error?.message ?? 'Unauthorized',
                }));
              }

              if (isRefreshRequest) {
                this.logoutAndRedirect();
                return throwError(() => new Error(this._translate.instant('sessionExpired')));
              }

              this._store.dispatch(setLoading({ isLoading: true }));

              return this.handle401Error(req, next).pipe(
                // Always stop loading regardless of refresh success/failure/silent completion.
                finalize(() => {
                  this._store.dispatch(setLoading({ isLoading: false }));
                }),
                catchError((refreshErr) => {
                  if (refreshErr instanceof HttpErrorResponse) {
                    const body = refreshErr.error;
                    const formatted =
                      body != null ? formatGuarantorErrorPayload(body) : '';
                    const fallback =
                      typeof body === 'object' &&
                      body !== null &&
                      'message' in body &&
                      typeof (body as { message?: unknown }).message === 'string'
                        ? String((body as { message: string }).message)
                        : '';
                    const apiMessage =
                      formatted ||
                      fallback ||
                      this._translate.instant('someThingWentWrong');
                    return throwError(() => new Error(apiMessage));
                  }
                  return throwError(() => refreshErr);
                })
              );
            }

            // Handle other HTTP error statuses
            let apiMessage = '';
            if (error.status === 0) {
              apiMessage = this._translate.instant('cannotConnectToServer');
            } 
            // else if (error.status === 500) {
            //   apiMessage = this._translate.instant('internalServerError');
            // } 
            else if (Number(error.status) === 503) {
              apiMessage = this._translate.instant('youHaveReachedTheRequestLimitPleaseTryAgainLater');
            } else if (error.status === 403) {
              apiMessage = this._translate.instant('accessDenied');
            // Check if it's EmailNotVerified error - preserve error structure for component handling
              const errorMessage = error?.error?.message;
              if (errorMessage === 'EmailNotVerified') {
                // Preserve the original error structure so component can handle it
                return throwError(() => ({
                  status: error.status,
                  error: error.error,
                  message: errorMessage
                }));
              }
            } else {
              // Prefer full LMS body (message + data[] + errors); avoid non-string message → [object Object] in toasts
              const body = error.error;
              const formatted =
                body != null ? formatGuarantorErrorPayload(body) : '';
              const fallback =
                typeof body === 'object' &&
                body !== null &&
                'message' in body &&
                typeof (body as { message?: unknown }).message === 'string'
                  ? String((body as { message: string }).message)
                  : '';
              apiMessage =
                formatted ||
                fallback ||
                this._translate.instant('someThingWentWrong');
            }

            return throwError(() => new Error(apiMessage));
          })
        );
      })
    );
  }

  private addHeaders(request: HttpRequest<any>, token: string | null, lang: string) {
    const headers: Record<string, string> = {
      'Accept-Language': lang || 'en',
      'X-System-Id': '1',
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return request.clone({ setHeaders: headers });
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this._authService.refreshToken().pipe(
        catchError((refreshError) => {
          this.isRefreshing = false;
          this.logoutAndRedirect();
          // Session expired: logout and complete silently to avoid URL-based toasts in feature components.
          return EMPTY;
        }),
        switchMap((res: any) => {
          const newToken = res?.accessToken;
          this._store.dispatch(signInSuccess({ response: res }));
          this.isRefreshing = false;
          if (newToken) {
            this._authService.saveToken(newToken);
            this.refreshTokenSubject.next(newToken);
            return next.handle(this.addHeaders(request, newToken, this.currentLang));
          } else {
            this.logoutAndRedirect();
            // Treat missing token as expired session and complete silently.
            return EMPTY;
          }
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) =>
          next.handle(this.addHeaders(request, token, this.currentLang))
        )
      );
    }
  }

  private logoutAndRedirect() {
    if (!this.isLoggingOut) {
      this.logoutSubject.next();
      this._store.dispatch(setLoading({ isLoading: false }));
    }
  }

  resetLogoutState() {
    this.isLoggingOut = false;
  }
}

